import BrainboxPort from '../lib/brainbox_port.js'

/**
 * ScreenshotCapture hook for capturing screenshots from the current tab
 * The extension API provides a UI for users to choose between full page or region capture
 */
const ScreenshotCapture = {
  mounted () {
    this.el.addEventListener('click', async (e) => {
      e.preventDefault()

      const maxSize = parseInt(this.el.dataset.maxSize || '10000000', 10)

      try {
        // Check if port is connected
        if (!BrainboxPort.isConnected) {
          this.pushEvent('flash', {
            type: 'error',
            message: 'Extension not connected. Please reload the page.'
          })
          return
        }

        // Build options for screenshot capture
        const options = {
          regionInstructionText: 'Drag to select a region to capture',
          fullPageInstructionText: 'Click anywhere to capture the full page',
          format: 'png'
        }

        // Call the extension API
        const result = await BrainboxPort.captureRegion(options)

        // Handle cancellation
        if (!result || !result.dataUrl) {
          // User cancelled - don't show error, just return silently
          return
        }

        // Extract the base64 data from the data URL
        // Data URL format: "data:image/png;base64,..."
        const matches = result.dataUrl.match(/^data:(.*?);base64,(.*)$/)
        if (!matches) {
          this.pushEvent('flash', {
            type: 'error',
            message: 'Invalid screenshot data received'
          })
          return
        }

        const mimeType = matches[1]
        const base64Data = matches[2]

        // Calculate approximate size (base64 is ~33% larger than binary)
        const approximateSize = Math.ceil((base64Data.length * 3) / 4)

        // Validate size
        if (approximateSize > maxSize) {
          const sizeMB = (approximateSize / 1_000_000).toFixed(1)
          const maxSizeMB = (maxSize / 1_000_000).toFixed(1)

          this.pushEvent('flash', {
            type: 'error',
            message: `Screenshot is too large (${sizeMB} MB). Maximum size is ${maxSizeMB} MB.`
          })
          return
        }

        // Generate filename with timestamp
        const extension = mimeType.split('/')[1] || 'png'
        const filename = `screenshot-${Date.now()}.${extension}`

        // Convert data URL to Blob
        const byteString = atob(base64Data)
        const arrayBuffer = new ArrayBuffer(byteString.length)
        const uint8Array = new Uint8Array(arrayBuffer)
        for (let i = 0; i < byteString.length; i++) {
          uint8Array[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([arrayBuffer], { type: mimeType })

        // Convert Blob to File
        const file = new File([blob], filename, { type: mimeType })

        // Add to LiveView upload system
        // Use data-phx-upload-ref to find the file input (more reliable than ID)
        const fileInput = document.querySelector('input[type="file"][data-phx-upload-ref]')
        if (!fileInput) {
          console.error('File upload input not found in DOM')
          this.pushEvent('flash', {
            type: 'error',
            message: 'File upload system not available'
          })
          return
        }

        const dataTransfer = new DataTransfer()
        dataTransfer.items.add(file)
        fileInput.files = dataTransfer.files

        // Trigger change event to notify LiveView
        fileInput.dispatchEvent(new Event('change', { bubbles: true }))

      } catch (err) {
        console.error('Screenshot capture error:', err)

        let errorMessage = 'Failed to capture screenshot'
        if (err.message) {
          errorMessage = `Screenshot error: ${err.message}`
        }

        this.pushEvent('flash', {
          type: 'error',
          message: errorMessage
        })
      }
    })
  }
}

export default ScreenshotCapture
